import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, Search, Building2, ChevronLeft, ChevronRight, ExternalLink, Ticket, Clock3, CheckCircle2, Layers3 } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import api from '../api/axios';
import { getAllTicketsSuperAdmin } from '../api/tickets';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CLOSED', label: 'Closed' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All priorities' },
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
];

const badge = (text, tone) => {
  const tones = {
    slate: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
    amber: 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200',
    red: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
    green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200',
    purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-semibold ${tones[tone] || tones.slate}`}>
      {text}
    </span>
  );
};

const priorityTone = (p) => {
  const u = String(p || '').toUpperCase();
  if (u === 'CRITICAL') return 'red';
  if (u === 'HIGH') return 'amber';
  if (u === 'MEDIUM') return 'blue';
  return 'slate';
};

const SuperAdminGlobalTickets = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0, limit: 25 });

  const fetchTenants = useCallback(async () => {
    try {
      const res = await api.get('/tenants');
      setTenants(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      setTenants([]);
    }
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAllTicketsSuperAdmin({
        tenant_id: tenantId || undefined,
        search: searchApplied || undefined,
        status: status || undefined,
        priority: priority || undefined,
        page,
        limit: 25,
      });
      if (res.success && res.data) {
        setTickets(res.data.tickets || []);
        setPagination({
          total: res.data.pagination?.total ?? 0,
          totalPages: res.data.pagination?.totalPages ?? 0,
          limit: res.data.pagination?.limit ?? 25,
        });
      } else {
        setTickets([]);
        toast.error(res.message || 'Failed to load tickets');
      }
    } catch (err) {
      setTickets([]);
      toast.error(err.response?.data?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [tenantId, searchApplied, status, priority, page]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const applySearch = (e) => {
    e?.preventDefault();
    setPage(1);
    setSearchApplied(searchInput.trim());
  };

  const clearFilters = () => {
    setTenantId('');
    setSearchInput('');
    setSearchApplied('');
    setStatus('');
    setPriority('');
    setPage(1);
  };

  const statusCountsOnPage = tickets.reduce(
    (acc, row) => {
      const s = String(row?.status || '').toUpperCase();
      if (s === 'OPEN' || s === 'IN_PROGRESS' || s === 'PENDING') acc.active += 1;
      if (s === 'RESOLVED' || s === 'CLOSED') acc.resolved += 1;
      return acc;
    },
    { active: 0, resolved: 0 }
  );
  const uniqueTenantsOnPage = new Set(tickets.map((row) => row?.tenant_id).filter(Boolean)).size;

  return (
    <DashboardLayout userRole="super_admin">
      <div className="w-full min-h-0 bg-slate-50 dark:bg-slate-900 pb-8">
        <div className="w-full max-w-none mx-auto space-y-6">
          <div className="rounded-2xl tenant-brand-gradient text-white p-6 shadow-lg shadow-indigo-500/20">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
                  <Globe className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Global ticket control</h1>
                  <p className="text-white/80 text-sm mt-1">
                    View and open tickets across every tenant. Use filters to narrow the list.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold">Filtered total</p>
                <Ticket className="w-4 h-4 text-indigo-500" />
              </div>
              <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{pagination.total}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">All matching tickets</p>
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold">Open workload</p>
                <Clock3 className="w-4 h-4 text-amber-500" />
              </div>
              <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{statusCountsOnPage.active}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">On this page (open/in progress/pending)</p>
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold">Resolved + closed</p>
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              </div>
              <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{statusCountsOnPage.resolved}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">On this page</p>
            </div>

            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold">Tenants shown</p>
                <Layers3 className="w-4 h-4 text-violet-500" />
              </div>
              <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{uniqueTenantsOnPage}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Unique tenants in current page</p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 p-4 sm:p-6 shadow-sm">
            <form onSubmit={applySearch} className="flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wide">
                    Tenant
                  </label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <select
                      value={tenantId}
                      onChange={(e) => {
                        setTenantId(e.target.value);
                        setPage(1);
                      }}
                      className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
                    >
                      <option value="">All tenants</option>
                      {tenants.map((t) => (
                        <option key={t.id} value={String(t.id)}>
                          {t.name} (#{t.id})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wide">
                    Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      setPage(1);
                    }}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value || 'all'} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wide">
                    Priority
                  </label>
                  <select
                    value={priority}
                    onChange={(e) => {
                      setPriority(e.target.value);
                      setPage(1);
                    }}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
                  >
                    {PRIORITY_OPTIONS.map((o) => (
                      <option key={o.value || 'all'} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wide">
                    Search
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="search"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Ticket # or subject"
                        className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
                      />
                    </div>
                    <button
                      type="submit"
                      className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shrink-0"
                    >
                      Search
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  Clear filters
                </button>
                <span className="text-slate-400 dark:text-slate-500">·</span>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  {pagination.total} ticket{pagination.total !== 1 ? 's' : ''} match
                </span>
              </div>
            </form>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 dark:bg-slate-900/80 text-slate-600 dark:text-slate-300 text-xs uppercase tracking-wide font-semibold">
                  <tr>
                    <th className="px-4 py-3">Ticket</th>
                    <th className="px-4 py-3">Tenant</th>
                    <th className="px-4 py-3">Subject</th>
                    <th className="px-4 py-3">Requester</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Priority</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3 w-24"> </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-16 text-center text-slate-500">
                        Loading tickets…
                      </td>
                    </tr>
                  ) : tickets.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-16 text-center text-slate-500">
                        No tickets match your filters.
                      </td>
                    </tr>
                  ) : (
                    tickets.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-900/40">
                        <td className="px-4 py-3 font-mono font-medium text-indigo-600 dark:text-indigo-400">
                          {row.ticket_number || `#${row.id}`}
                        </td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-200">
                          {row.tenant_name || '—'}
                          {row.tenant_slug ? (
                            <span className="block text-xs text-slate-400 font-normal">{row.tenant_slug}</span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-slate-800 dark:text-slate-100 max-w-xs truncate" title={row.subject}>
                          {row.subject}
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                          <span className="block truncate max-w-[140px]" title={row.customer_name}>
                            {row.customer_name || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">{badge(row.status || '—', 'purple')}</td>
                        <td className="px-4 py-3">{badge(row.priority || '—', priorityTone(row.priority))}</td>
                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400 whitespace-nowrap">
                          {row.created_at
                            ? new Date(row.created_at).toLocaleString(undefined, {
                                dateStyle: 'short',
                                timeStyle: 'short',
                              })
                            : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => navigate(`/tickets/${row.id}`)}
                            className="inline-flex items-center gap-1 text-indigo-600 dark:text-indigo-400 hover:underline text-xs font-semibold"
                          >
                            Open <ExternalLink className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30">
                <p className="text-xs text-slate-500">
                  Page {page} of {pagination.totalPages}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-sm disabled:opacity-40"
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </button>
                  <button
                    type="button"
                    disabled={page >= pagination.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-sm disabled:opacity-40"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminGlobalTickets;
